import { useEffect, useState } from "react";
import { useLocation, useNavigate, useParams, Link } from "react-router-dom";
import { MatchmakerLayout } from "@/components/matchmaker/MatchmakerLayout";
import { AdminLayout } from "@/components/admin/AdminLayout";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Skeleton } from "@/components/ui/skeleton";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Archive,
  ArchiveRestore,
  ArrowLeft,
  Check,
  Info,
  Loader2,
  Mail,
  MessageSquare,
  Phone,
  X,
} from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";
import { useUserRole } from "@/hooks/useUserRole";
import { useSignedPhotoUrl } from "@/lib/photoUrl";
import {
  anonymizeName,
  anonymizeEmail,
  anonymizePhone,
  anonymizeText,
} from "@/utils/anonymization";

type Profile = any;

interface ContactInfo {
  email: string | null;
  phone: string | null;
  whatsapp: string | null;
  preferred_contact_method: string | null;
}

interface ProfileReferenceRow {
  name: string | null;
  relationship: string | null;
  phone: string | null;
  email: string | null;
}

export default function MatchmakerJoinRequestDetail() {
  const { profileId } = useParams<{ profileId: string }>();
  const navigate = useNavigate();
  const { isAdmin } = useUserRole();
  const location = useLocation();

  const [loading, setLoading] = useState(true);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [contactInfo, setContactInfo] = useState<ContactInfo | null>(null);
  const [references, setReferences] = useState<ProfileReferenceRow[]>([]);
  const [errorState, setErrorState] = useState<string | null>(null);

  const [isApproving, setIsApproving] = useState(false);
  const [isDeclining, setIsDeclining] = useState(false);
  const [declineDialogOpen, setDeclineDialogOpen] = useState(false);
  const [isArchiving, setIsArchiving] = useState(false);

  const signedProfilePicture = useSignedPhotoUrl(profile?.profile_picture_url ?? undefined);

  const listPath = location.pathname.startsWith("/admin")
    ? "/admin/join-requests"
    : "/matchmaker/join-requests";
  const isArchived = Boolean(profile?.archived_at);
  const busy = isApproving || isDeclining || isArchiving;

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      if (!profileId) {
        setErrorState("Missing profile id.");
        setLoading(false);
        return;
      }
      try {
        setLoading(true);
        setErrorState(null);

        const { data: profileData, error: profileErr } = await supabase
          .from("profiles")
          .select("*")
          .eq("id", profileId)
          .maybeSingle();

        if (profileErr) throw profileErr;

        if (!profileData) {
          if (!cancelled) {
            setErrorState("Profile not found.");
            setProfile(null);
          }
          return;
        }

        if (
          profileData.status !== "pending" ||
          profileData.submission_source !== "self"
        ) {
          if (!cancelled) {
            setProfile(profileData);
            setErrorState(
              "This profile is not a pending self-submitted join request.",
            );
          }
          return;
        }

        const { data: contactData, error: contactErr } = await supabase
          .from("contact_info")
          .select("email, phone, whatsapp, preferred_contact_method")
          .eq("profile_id", profileId)
          .maybeSingle();

        if (contactErr) throw contactErr;

        const { data: referenceData, error: referenceErr } = await supabase
          .from("profile_references")
          .select("name, relationship, phone, email")
          .eq("profile_id", profileId);

        if (referenceErr) throw referenceErr;

        if (!cancelled) {
          setProfile(profileData);
          setContactInfo((contactData as ContactInfo | null) ?? null);
          setReferences((referenceData as ProfileReferenceRow[] | null) ?? []);
        }
      } catch (err: any) {
        console.error("join_request_detail.load_failed");
        if (!cancelled) {
          setErrorState(err.message || "Failed to load profile.");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [profileId]);

  const handleApprove = async () => {
    if (!profileId || isApproving) return;
    setIsApproving(true);
    try {
      const { data, error } = await supabase.functions.invoke(
        "approve-join-request",
        { body: { profile_id: profileId } },
      );

      if (error) throw error;

      if (data?.warning) {
        toast({
          title: "Approved, but email failed",
          description: `${data.warning}${
            data.magic_link
              ? `\nManual link: ${data.magic_link}`
              : ""
          }`,
          variant: "destructive",
        });
      } else {
        toast({
          title: "Request approved",
          description: "Invite email sent.",
        });
      }
      navigate(listPath);
    } catch (err: any) {
      console.error("join_request.approve_failed");
      toast({
        title: "Error",
        description: err.message || "Failed to approve request.",
        variant: "destructive",
      });
    } finally {
      setIsApproving(false);
    }
  };

  const handleDeclineConfirm = async () => {
    if (!profileId || isDeclining) return;
    setIsDeclining(true);
    try {
      const { error } = await supabase.functions.invoke(
        "decline-join-request",
        { body: { profile_id: profileId } },
      );

      if (error) throw error;

      toast({
        title: "Request declined",
      });
      setDeclineDialogOpen(false);
      navigate(listPath);
    } catch (err: any) {
      console.error("join_request.decline_failed");
      toast({
        title: "Error",
        description: err.message || "Failed to decline request.",
        variant: "destructive",
      });
    } finally {
      setIsDeclining(false);
    }
  };

  const handleToggleArchive = async () => {
    if (!profileId || busy) return;
    const archive = !isArchived;
    setIsArchiving(true);
    try {
      const { error } = await supabase.rpc("set_join_request_archived", {
        _profile_id: profileId,
        _archived: archive,
      });
      if (error) throw error;
      toast({
        title: archive ? "Request archived" : "Request restored",
        description: archive
          ? "Moved to Archived. The applicant is not notified."
          : "Moved back to Pending.",
      });
      navigate(archive ? listPath : `${listPath}?view=archived`);
    } catch (err: any) {
      console.error(archive ? "join_request.archive_failed" : "join_request.unarchive_failed");
      toast({
        title: "Error",
        description: err.message || "Failed to update request.",
        variant: "destructive",
      });
    } finally {
      setIsArchiving(false);
    }
  };

  const Layout = isAdmin ? AdminLayout : MatchmakerLayout;

  return (
    <Layout title="Join Request">
      <div className="container mx-auto p-6 max-w-4xl">
        <div className="mb-6">
          <Link
            to={listPath}
            className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to requests
          </Link>
        </div>

        {loading ? (
          <div className="space-y-4">
            <Skeleton className="h-32 w-full" />
            <Skeleton className="h-64 w-full" />
            <Skeleton className="h-64 w-full" />
          </div>
        ) : errorState ? (
          <Alert variant="destructive">
            <Info className="h-4 w-4" />
            <AlertTitle>Cannot review request</AlertTitle>
            <AlertDescription className="space-y-3">
              <p>{errorState}</p>
              <Button variant="outline" onClick={() => navigate(listPath)}>
                Back to list
              </Button>
            </AlertDescription>
          </Alert>
        ) : profile ? (
          <div className="space-y-6">
            {/* Header */}
            <Card className="p-6">
              <div className="flex items-start gap-4">
                <Avatar className="h-24 w-24">
                  {signedProfilePicture ? (
                    <AvatarImage
                      src={signedProfilePicture}
                      alt={profile.first_name}
                    />
                  ) : null}
                  <AvatarFallback className="text-2xl">
                    {isAdmin
                      ? "?"
                      : profile.first_name?.[0]?.toUpperCase() ?? "?"}
                  </AvatarFallback>
                </Avatar>
                <div className="flex-1">
                  <div className="flex items-center gap-2 flex-wrap mb-1">
                    <h3 className="text-2xl font-bold">
                      {isAdmin ? anonymizeName(profile.first_name) : profile.first_name}
                      {profile.age ? `, ${profile.age}` : ""}
                    </h3>
                    <Badge variant="outline">Pending</Badge>
                    {isArchived && <Badge variant="secondary">Archived</Badge>}
                    <Badge variant="outline">Self-submitted</Badge>
                  </div>
                  <p className="text-muted-foreground capitalize">
                    {profile.gender === "male"
                      ? "Brother"
                      : profile.gender === "female"
                        ? "Sister"
                        : profile.gender}
                    {profile.location ? ` • ${profile.location}` : ""}
                  </p>
                </div>
              </div>
            </Card>

            {/* Contact Info */}
            <Card className="p-6 space-y-3">
              <h4 className="font-semibold text-lg">Contact Information</h4>
              {contactInfo ? (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                  {contactInfo.email && (
                    <div className="flex items-center gap-2">
                      <Mail className="h-4 w-4 text-muted-foreground" />
                      <span>{isAdmin ? anonymizeEmail(contactInfo.email) : contactInfo.email}</span>
                    </div>
                  )}
                  {contactInfo.phone && (
                    <div className="flex items-center gap-2">
                      <Phone className="h-4 w-4 text-muted-foreground" />
                      <span>{isAdmin ? anonymizePhone(contactInfo.phone) : contactInfo.phone}</span>
                    </div>
                  )}
                  {contactInfo.whatsapp && (
                    <div className="flex items-center gap-2">
                      <MessageSquare className="h-4 w-4 text-muted-foreground" />
                      <span>WhatsApp: {isAdmin ? anonymizePhone(contactInfo.whatsapp) : contactInfo.whatsapp}</span>
                    </div>
                  )}
                  {contactInfo.preferred_contact_method && (
                    <div>
                      <span className="text-muted-foreground">Preferred: </span>
                      <span className="capitalize">
                        {contactInfo.preferred_contact_method}
                      </span>
                    </div>
                  )}
                  {!contactInfo.email &&
                    !contactInfo.phone &&
                    !contactInfo.whatsapp && (
                      <p className="text-sm text-muted-foreground col-span-2">
                        No contact details on file.
                      </p>
                    )}
                </div>
              ) : (
                <p className="text-sm text-muted-foreground">
                  No contact info row was submitted with this profile.
                </p>
              )}
            </Card>

            {/* Basic Info */}
            <Card className="p-6 space-y-3">
              <h4 className="font-semibold text-lg">Basic Information</h4>
              <div className="grid grid-cols-2 gap-3 text-sm">
                {profile.ethnicity && (
                  <Field label="Ethnicity" value={profile.ethnicity} />
                )}
                {profile.marital_status && (
                  <Field
                    label="Marital Status"
                    value={profile.marital_status}
                    capitalize
                  />
                )}
                {profile.citizenship_status && (
                  <Field
                    label="Citizenship"
                    value={profile.citizenship_status}
                  />
                )}
                {profile.height_feet ? (
                  <Field
                    label="Height"
                    value={`${profile.height_feet}'${profile.height_inches || 0}"`}
                  />
                ) : null}
                {profile.has_children !== undefined &&
                  profile.has_children !== null && (
                    <Field
                      label="Has Children"
                      value={
                        profile.has_children
                          ? `Yes (${profile.children_count || 0})`
                          : "No"
                      }
                    />
                  )}
                {profile.willing_to_relocate !== undefined &&
                  profile.willing_to_relocate !== null && (
                    <Field
                      label="Willing to Relocate"
                      value={profile.willing_to_relocate ? "Yes" : "No"}
                    />
                  )}
              </div>
            </Card>

            {/* Religious Profile */}
            <Card className="p-6 space-y-3">
              <h4 className="font-semibold text-lg">Religious Profile</h4>
              <div className="grid grid-cols-2 gap-3 text-sm">
                {profile.religiosity_level && (
                  <Field
                    label="Religiosity"
                    value={profile.religiosity_level}
                    capitalize
                  />
                )}
                {profile.prayer_frequency && (
                  <Field
                    label="Prayer Frequency"
                    value={profile.prayer_frequency}
                  />
                )}
                {profile.sect && (
                  <Field label="Sect" value={profile.sect} capitalize />
                )}
                {profile.masjid_attendance && (
                  <Field
                    label="Masjid Attendance"
                    value={profile.masjid_attendance}
                  />
                )}
                {profile.quran_knowledge && (
                  <Field
                    label="Quran Knowledge"
                    value={profile.quran_knowledge}
                  />
                )}
                {profile.islamic_knowledge_level && (
                  <Field
                    label="Islamic Knowledge"
                    value={profile.islamic_knowledge_level}
                  />
                )}
                {profile.importance_of_islam !== undefined &&
                  profile.importance_of_islam !== null && (
                    <Field
                      label="Importance of Islam"
                      value={`${profile.importance_of_islam}/10`}
                    />
                  )}
                {profile.gender === "male" && profile.has_beard && (
                  <Field
                    label="Has Beard"
                    value={profile.has_beard}
                    capitalize
                  />
                )}
                {profile.gender === "female" &&
                  profile.wears_hijab !== undefined &&
                  profile.wears_hijab !== null && (
                    <Field
                      label="Wears Hijab"
                      value={profile.wears_hijab ? "Yes" : "No"}
                    />
                  )}
                {profile.gender === "female" &&
                  profile.wears_hijab &&
                  profile.wears_hijab_level && (
                    <Field
                      label="Hijab Level"
                      value={profile.wears_hijab_level}
                      capitalize
                    />
                  )}
              </div>
            </Card>

            {/* Education & Career */}
            <Card className="p-6 space-y-3">
              <h4 className="font-semibold text-lg">Education & Career</h4>
              <div className="grid gap-3 text-sm">
                {profile.education_level && (
                  <Field
                    label="Education Level"
                    value={profile.education_level}
                  />
                )}
                {profile.field_of_study && (
                  <Field
                    label="Field of Study"
                    value={profile.field_of_study}
                  />
                )}
                {profile.employment_status && (
                  <Field
                    label="Employment Status"
                    value={profile.employment_status}
                    capitalize
                  />
                )}
                {profile.occupation && (
                  <Field label="Occupation" value={profile.occupation} />
                )}
                {profile.career_description && (
                  <div>
                    <span className="text-muted-foreground">
                      Career Description:
                    </span>
                    <p className="text-sm whitespace-pre-wrap mt-1">
                      {isAdmin ? anonymizeText(profile.career_description) : profile.career_description}
                    </p>
                  </div>
                )}
                {profile.financial_status !== undefined &&
                  profile.financial_status !== null && (
                    <Field
                      label="Financial Status"
                      value={`${profile.financial_status}/10`}
                    />
                  )}
              </div>
            </Card>

            {/* Lifestyle */}
            <Card className="p-6 space-y-3">
              <h4 className="font-semibold text-lg">Lifestyle</h4>
              <div className="grid grid-cols-2 gap-3 text-sm">
                {profile.smoking && (
                  <Field label="Smoking" value={profile.smoking} capitalize />
                )}
                {profile.exercise_routine && (
                  <Field
                    label="Exercise Routine"
                    value={profile.exercise_routine}
                    capitalize
                  />
                )}
                {profile.dietary_preferences &&
                  profile.dietary_preferences.length > 0 && (
                    <div className="col-span-2">
                      <span className="text-muted-foreground">
                        Dietary Preferences:
                      </span>
                      <div className="flex flex-wrap gap-1 mt-1">
                        {profile.dietary_preferences.map((pref: string) => (
                          <Badge key={pref} variant="outline" className="text-xs">
                            {pref}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  )}
              </div>
            </Card>

            {/* What They're Seeking */}
            <Card className="p-6 space-y-3">
              <h4 className="font-semibold text-lg">What They're Seeking</h4>
              <div className="grid grid-cols-2 gap-3 text-sm">
                {(profile.seeking_age_min || profile.seeking_age_max) && (
                  <Field
                    label="Age Range"
                    value={`${profile.seeking_age_min ?? "?"} - ${profile.seeking_age_max ?? "?"}`}
                  />
                )}
                {profile.seeking_location_preference && (
                  <Field
                    label="Location"
                    value={profile.seeking_location_preference}
                  />
                )}
                {profile.seeking_height_min_feet && (
                  <Field
                    label="Height Range"
                    value={`${profile.seeking_height_min_feet}'${profile.seeking_height_min_inches || 0}" - ${profile.seeking_height_max_feet}'${profile.seeking_height_max_inches || 0}"`}
                  />
                )}
              </div>
              {profile.seeking_description && (
                <>
                  <Separator />
                  <div>
                    <span className="text-muted-foreground text-sm">
                      Additional Details:
                    </span>
                    <p className="text-sm whitespace-pre-wrap mt-1">
                      {isAdmin ? anonymizeText(profile.seeking_description) : profile.seeking_description}
                    </p>
                  </div>
                </>
              )}
            </Card>

            {/* Bio */}
            {profile.bio && (
              <Card className="p-6 space-y-3">
                <h4 className="font-semibold text-lg">About</h4>
                <p className="text-sm text-muted-foreground whitespace-pre-wrap">
                  {isAdmin ? anonymizeText(profile.bio) : profile.bio}
                </p>
              </Card>
            )}

            {/* Connection to the Community */}
            {profile.community_connection && (
              <Card className="p-6 space-y-3">
                <h4 className="font-semibold text-lg">Connection to the Community</h4>
                <p className="text-sm text-muted-foreground whitespace-pre-wrap">
                  {isAdmin ? anonymizeText(profile.community_connection) : profile.community_connection}
                </p>
              </Card>
            )}

            {/* References (private — matchmakers only) */}
            <Card className="p-6 space-y-3">
              <h4 className="font-semibold text-lg">References</h4>
              {references.length > 0 ? (
                <div className="space-y-4">
                  {references.map((ref, i) => (
                    <div
                      key={i}
                      className="rounded-md border border-border bg-muted/20 p-4 text-sm space-y-1"
                    >
                      <p className="font-medium">
                        {isAdmin ? anonymizeName(ref.name) : ref.name}
                        {ref.relationship ? (
                          <span className="font-normal text-muted-foreground">
                            {" "}— {ref.relationship}
                          </span>
                        ) : null}
                      </p>
                      {ref.phone && (
                        <div className="flex items-center gap-2 text-muted-foreground">
                          <Phone className="h-3.5 w-3.5" />
                          <span>{isAdmin ? anonymizePhone(ref.phone) : ref.phone}</span>
                        </div>
                      )}
                      {ref.email && (
                        <div className="flex items-center gap-2 text-muted-foreground">
                          <Mail className="h-3.5 w-3.5" />
                          <span>{isAdmin ? anonymizeEmail(ref.email) : ref.email}</span>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-muted-foreground">
                  No references were submitted with this profile.
                </p>
              )}
            </Card>

            {/* Matchmaker Notes */}
            {profile.matchmaker_notes && (
              <Card className="p-6 bg-blue-50 dark:bg-blue-950/20 border-blue-200 dark:border-blue-800">
                <h4 className="font-semibold text-lg mb-2 text-blue-900 dark:text-blue-100">
                  Notes to Matchmaker
                </h4>
                <p className="text-sm text-blue-800 dark:text-blue-200 whitespace-pre-wrap">
                  {isAdmin ? '[REDACTED - Content visible to matchmakers only]' : profile.matchmaker_notes}
                </p>
              </Card>
            )}

            {/* Action Buttons */}
            <Card className="p-6 sticky bottom-4 bg-background border-2">
              <div className="flex flex-col sm:flex-row gap-3 justify-end">
                <Button
                  variant="outline"
                  size="lg"
                  onClick={handleToggleArchive}
                  disabled={busy}
                  className="sm:mr-auto"
                >
                  {isArchiving ? (
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  ) : isArchived ? (
                    <ArchiveRestore className="mr-2 h-4 w-4" />
                  ) : (
                    <Archive className="mr-2 h-4 w-4" />
                  )}
                  {isArchived ? "Restore to Pending" : "Archive"}
                </Button>
                <Button
                  variant="outline"
                  size="lg"
                  onClick={() => setDeclineDialogOpen(true)}
                  disabled={busy}
                  className="text-destructive hover:bg-destructive hover:text-destructive-foreground"
                >
                  <X className="mr-2 h-4 w-4" />
                  Decline
                </Button>
                <Button
                  size="lg"
                  onClick={handleApprove}
                  disabled={busy}
                >
                  {isApproving ? (
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  ) : (
                    <Check className="mr-2 h-4 w-4" />
                  )}
                  Approve
                </Button>
              </div>
            </Card>
          </div>
        ) : null}
      </div>

      <AlertDialog
        open={declineDialogOpen}
        onOpenChange={(open) => {
          if (!isDeclining) setDeclineDialogOpen(open);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Decline this join request?</AlertDialogTitle>
            <AlertDialogDescription>
              The applicant will not receive an invite. This action marks the
              profile as declined and cannot be undone here.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeclining}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault();
                handleDeclineConfirm();
              }}
              disabled={isDeclining}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {isDeclining ? "Declining..." : "Decline Request"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Layout>
  );
}

function Field({
  label,
  value,
  capitalize,
}: {
  label: string;
  value: string | number;
  capitalize?: boolean;
}) {
  return (
    <div>
      <span className="text-muted-foreground">{label}:</span>
      <p className={`font-medium ${capitalize ? "capitalize" : ""}`}>{value}</p>
    </div>
  );
}
